/**
 * Texte rund um Papierkorb und Rueckgaengig (F-45) - rein, damit sie sich
 * ohne Oberflaeche pruefen lassen. Die Woerter stehen in `i18n/de.ts`.
 */
import type { WiederherstellErgebnis } from '@/db/papierkorb/papierkorb';
import type { AktionsMeldung, Hinweis, PapierkorbEintrag } from '@/domain/papierkorb/typen';
import { tabellenName } from '@/modules/sync/beschriftung';
import { de } from '@/i18n/de';

const t = de.papierkorb;

/** „„Umzug“ gelöscht“, „3 Einträge gelöscht“, „Erledigt: „Einkaufen““ … */
export function meldungText(m: Pick<AktionsMeldung, 'art' | 'titel' | 'anzahl'>): string {
  if (m.art === 'geloescht' && m.anzahl > 1) return t.mehrereGeloescht(m.anzahl);
  return t.meldung[m.art](m.titel || t.ohneTitel);
}

/** Was nicht zurueckkam - `geaendert` (angelegte Zeile bleibt stehen) zaehlt nicht. */
const hindernis = (hinweise: readonly Hinweis[]): Hinweis | undefined => hinweise.find((h) => h.grund !== 'geaendert');

/**
 * Rueckmeldung nach dem Wiederherstellen eines Papierkorb-Eintrags.
 * „Endgültig gelöscht“ nur, wenn es den Eintrag nicht mehr gibt; sonst sagt
 * der erste Hinweis, woran es liegt (Datei auf dem anderen Geraet, erst das
 * Elternteil wiederherstellen ...).
 */
export function wiederherstellText(r: WiederherstellErgebnis | 'unvollstaendig' | null, titel: string): { text: string; fehler: boolean } {
  if (r === null) return { text: t.gehtNicht, fehler: true };
  if (r === 'unvollstaendig') return { text: t.unvollstaendig, fehler: true };
  const name = r.titel || titel || t.ohneTitel;
  if (!r.rest) return { text: r.zurueck ? t.wiederDa(name) : t.schonDa(name), fehler: false };
  const grund = hindernis(r.hinweise);
  if (!r.zurueck) return { text: grund ? hinweisText(grund) : t.gehtNicht, fehler: true };
  const dateien = r.hinweise.filter((h) => h.grund === 'datei_fehlt').length;
  return { text: dateien && !r.hinweise.some((h) => h.grund !== 'datei_fehlt') ? t.nurAufAnderemGeraet(dateien) : t.teilweise, fehler: false };
}

/** Was fehlt, damit die Zeile zurueck kann - fuer „Geht nicht mehr – „Einkauf“ fehlt inzwischen“. */
function fehlendes(h: Hinweis): string | null {
  if (h.grund === 'eltern_fehlen' && h.eltern) return h.eltern.titel || tabellenName(h.eltern.t);
  if (h.grund === 'fehlt') return h.eltern?.titel || h.titel || tabellenName(h.t);
  return null;
}

/**
 * Rueckmeldung nach dem Zuruecknehmen einer Aktion aus dem Speicher. Das
 * geht ganz oder gar nicht (siehe `zuruecknehmen`); eine inzwischen
 * geaenderte angelegte Zeile bleibt stehen und wird genannt.
 */
export function zuruecknehmenText(r: { zurueck: number; hinweise?: readonly Hinweis[] }, titel: string): { text: string; fehler: boolean } {
  const hinweise = r.hinweise ?? [];
  if (!r.zurueck) {
    const grund = hindernis(hinweise);
    const was = grund && fehlendes(grund);
    return { text: was ? t.gehtNichtFehlt(was) : grund ? hinweisText(grund) : t.gehtNicht, fehler: true };
  }
  const stehen = hinweise.find((h) => h.grund === 'geaendert');
  if (stehen) return { text: t.zurueckGenommenGeaendert(stehen.titel || tabellenName(stehen.t)), fehler: false };
  return { text: t.zurueckGenommen(titel || t.ohneTitel), fehler: false };
}

export function hinweisText(h: Hinweis): string {
  const name = h.titel || tabellenName(h.t);
  if (h.grund === 'fehlt') return t.hinweise.fehlt(h.eltern?.titel || name);
  if (h.grund === 'eltern_fehlen' && h.eltern?.titel) return t.elternFehlenMit(name, h.eltern.titel);
  return t.hinweise[h.grund](name);
}

const name = (tabelle: string, n: number): string => {
  const paar = t.mehrzahl[tabelle];
  return paar ? paar[n === 1 ? 0 : 1] : `× ${tabellenName(tabelle)}`;
};

/**
 * Was beim Wiederherstellen ausser der Hauptzeile noch passiert - direkt
 * sichtbar statt hinter einem Klick (Leitlinie 2). Die Hauptzeilen werden
 * herausgerechnet: bei einer Loeschung zuerst aus den geloeschten, sonst aus
 * den geaenderten Zeilen ihrer Tabelle.
 */
export function nebenwirkungen(kopf: Pick<PapierkorbEintrag, 'art' | 'tabelle' | 'zeileIds' | 'zaehler'>): string[] {
  const out: string[] = [];
  for (const [tabelle, z] of Object.entries(kopf.zaehler)) {
    let { weg, geaendert } = z;
    if (tabelle === kopf.tabelle) {
      let haupt = kopf.zeileIds.length;
      if (kopf.art === 'geloescht') {
        const w = Math.min(weg, haupt);
        weg -= w;
        haupt -= w;
      }
      geaendert -= Math.min(geaendert, haupt);
    }
    if (geaendert) out.push(t.wiederAngehaengt(geaendert, name(tabelle, geaendert)));
    if (weg) out.push(t.kommtMit(weg, name(tabelle, weg)));
    if (z.neu) out.push(t.wirdEntfernt(z.neu, name(tabelle, z.neu)));
  }
  return out;
}
